import { safeParse, removeNulls, safeParseNullDefault } from 'shouldve-been-js';
import ChecklistType from '../../Enums/ChecklistType';
import { checklistElementHelperFromIndexedDB, checklistElementHelperFromJson } from '../../Utils/ChecklistElementFactoryHelpers';
import type ChecklistElement from './Elements/ChecklistElementModel';

class ChecklistModel {

    private _checklistId: string;
    private _caption: string;
    private _description: string;
    private _checklistType: ChecklistType;
    private _elements: ChecklistElement[];

    public constructor(checklistId: string, caption: string, description: string, checklistType: ChecklistType, elements: ChecklistElement[]) {

        this._checklistId = checklistId;
        this._caption = caption;
        this._description = description;
        this._checklistType = checklistType;
        this._elements = elements;
    }

    public get checklistId(): string {
        return this._checklistId;
    }

    public set checklistId(value: string) {
        this._checklistId = value;
    }

    public get caption(): string {
        return this._caption;
    }

    public set caption(value: string) {
        this._caption = value;
    }

    public get description(): string {
        return this._description;
    }

    public set description(value: string) {
        this._description = value;
    }

    public get checklistType(): ChecklistType {
        return this._checklistType;
    }

    public set checklistType(value: ChecklistType) {
        this._checklistType = value;
    }

    public get elements(): ChecklistElement[] {
        return this._elements;
    }

    public set elements(value: ChecklistElement[]) {
        this._elements = value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromJson(key: string, json: any): ChecklistModel | null {

        const checklistId: string = key;
        const caption: string = safeParse<string>(json?.caption, '');
        const description: string = safeParse<string>(json?.description, '');
        const status: string = safeParse<string>(json?.status, 'D');
        const checklistTypeString: string = safeParse<string>(json?.checklistType, '');

        let checklistType: ChecklistType | null = null;
        if (checklistTypeString === 'checklist') {
            checklistType = ChecklistType.CHECKLIST;
        } else if (checklistTypeString === 'custom') {
            checklistType = ChecklistType.CUSTOM;
        }

        if (status === 'D' || checklistType === null) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        const elements: (ChecklistElement | null)[] = Object.entries(json?.components).map(([elementKey, elementValue]: [string, any]) => {
            return checklistElementHelperFromJson(elementKey, elementValue, checklistType);
        });   

        return new ChecklistModel(checklistId, caption, description, checklistType, removeNulls(elements));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromIndexedDB(object: any): ChecklistModel | null {

        const checklistId: string | null = safeParseNullDefault<string>(object?._checklistId, '');
        const caption: string = safeParse<string>(object?._caption, '');
        const description: string = safeParse<string>(object?._description, '');
        const checklistTypeNumber: number | null = safeParseNullDefault<number>(object?._checklistType, 0);

        let checklistType: ChecklistType | null = null;
        if (checklistTypeNumber === 0) {
            checklistType = ChecklistType.CHECKLIST;
        } else if (checklistTypeNumber === 1) {
            checklistType = ChecklistType.CUSTOM;
        }

        if (checklistId === null || checklistType === null) {
            return null;
        }

        if (!Array.isArray(object?._elements)) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const elements: (ChecklistElement | null)[] = object._elements.map((elementObject: any) => {
            return checklistElementHelperFromIndexedDB(elementObject);
        });   

        return new ChecklistModel(checklistId, caption, description, checklistType, removeNulls(elements));
    }
}

export default ChecklistModel;